/**
 * Lezen/schrijven van per-box tekst/vul-opmaak (`BoxStyle`) voor de custom
 * render-paden (Roadmap + Roadmap-matrix). Opslag als stash-entry in
 * `Diagram.viewState[DIAGRAM_BOX_STYLES_KEY]` → `Record<boxKey, BoxStyle>`,
 * zelfde patroon als `roadmapMatrixConfig.ts` (geen DB-migratie).
 */
import { type BoxStyle, DIAGRAM_BOX_STYLES_KEY, type ViewState } from "@ae/domain";

export function readBoxStyles(viewState: ViewState | undefined | null): Record<string, BoxStyle> {
  const entry = viewState?.[DIAGRAM_BOX_STYLES_KEY];
  const blob = entry?.style as Record<string, unknown> | undefined;
  if (!blob) return {};
  const out: Record<string, BoxStyle> = {};
  for (const [key, raw] of Object.entries(blob)) {
    if (!raw || typeof raw !== "object") continue;
    const s = raw as Record<string, unknown>;
    const bs: BoxStyle = {};
    if (typeof s.fontFamily === "string") bs.fontFamily = s.fontFamily;
    if (typeof s.fontSize === "number") bs.fontSize = s.fontSize;
    if (typeof s.fontColor === "string") bs.fontColor = s.fontColor;
    if (typeof s.fillColor === "string") bs.fillColor = s.fillColor;
    if (s.textAlign === "left" || s.textAlign === "center" || s.textAlign === "right") {
      bs.textAlign = s.textAlign;
    }
    if (s.verticalAlign === "top" || s.verticalAlign === "middle" || s.verticalAlign === "bottom") {
      bs.verticalAlign = s.verticalAlign;
    }
    if (Object.keys(bs).length > 0) out[key] = bs;
  }
  return out;
}

/** Merge een patch in de box-style en geef een nieuwe viewState terug (mutates niet). */
export function writeBoxStyle(viewState: ViewState, boxKey: string, patch: BoxStyle): ViewState {
  const current = readBoxStyles(viewState);
  const merged: Record<string, unknown> = { ...(current[boxKey] ?? {}), ...patch };
  // Lege/ongedefinieerde waarden verwijderen zodat "wissen" echt wist.
  for (const k of Object.keys(merged)) {
    const v = merged[k];
    if (v === undefined || v === null || v === "") delete merged[k];
  }
  const next: Record<string, unknown> = { ...current };
  if (Object.keys(merged).length > 0) next[boxKey] = merged;
  else delete next[boxKey];
  return {
    ...viewState,
    [DIAGRAM_BOX_STYLES_KEY]: { type: "object", ref: DIAGRAM_BOX_STYLES_KEY, style: next },
  };
}

/** Box-niveau CSS: vul-kleur + verticale positionering (flex justifyContent). */
export function boxFillCss(style: BoxStyle | undefined): React.CSSProperties {
  if (!style) return {};
  const css: React.CSSProperties = {};
  if (style.fillColor) css.backgroundColor = style.fillColor;
  if (style.verticalAlign) {
    css.justifyContent =
      style.verticalAlign === "top"
        ? "flex-start"
        : style.verticalAlign === "bottom"
          ? "flex-end"
          : "center";
  }
  return css;
}

/** Tekst-niveau CSS: lettertype, grootte, kleur en horizontale uitlijning.
 *  Toe te passen op het tekst-element (of een container waarvan de tekst
 *  erft) zodat 't bestaande utility-classes overschrijft. */
export function boxTextCss(style: BoxStyle | undefined): React.CSSProperties {
  if (!style) return {};
  const css: React.CSSProperties = {};
  if (style.fontFamily) css.fontFamily = style.fontFamily;
  if (style.fontSize) css.fontSize = `${style.fontSize}px`;
  if (style.fontColor) css.color = style.fontColor;
  if (style.textAlign) css.textAlign = style.textAlign;
  return css;
}
